import { WarningAmber } from "@mui/icons-material";
import { Tooltip, Typography } from "@mui/material";
import { parsePhoneNumberWithError } from "libphonenumber-js";

class PhoneUtils {
  static renderer(value: string) {
    try {
      if (!value) return "";
      if (value.startsWith("sip:")) {
        return value;
      }

      if (value.trim() !== "") {
        const num = parsePhoneNumberWithError(value, "US");
        if (num?.isValid()) {
          return `${
            num.country ? `[${num.country}]` : ""
          }${num.formatInternational()}`;
        }
      }
      return value.trim();
    } catch {
      // Return the original value without error markup for invalid numbers
      return value.trim() || "Unavailable";
    }
  }

  static rendererComponent(value: string) {
    try {
      if (!value) return "";
      if (value?.startsWith("sip:")) {
        return <Typography fontSize="inherit"> {value}</Typography>;
      }

      if (value?.trim() !== "") {
        const num = parsePhoneNumberWithError(value);
        if (num?.isValid()) {
          return (
            <Typography fontSize="inherit">{`[${
              num.country
            }] ${num.formatInternational()}`}</Typography>
          );
        }
      }
      return <Typography fontSize="inherit">{value?.trim()}</Typography>;
    } catch (err: any) {
      const readableMessage = err.message
        ? err.message
            .replace(/_/g, " ")
            .replace(/\b\w/g, (char: any) => char.toUpperCase())
        : "An unknown error occurred.";
      return (
        <Typography display="flex" alignItems="center" fontSize="inherit">
          <Tooltip title={readableMessage}>
            <WarningAmber color="warning" sx={{ fontSize: "1rem", mr: 0.5 }} />
          </Tooltip>
          {value}
        </Typography>
      );
    }
  }

  static rendererLocal(value: string) {
    try {
      if (!value) return "";
      if (value.startsWith("sip:")) {
        return value;
      }

      if (value.trim() !== "") {
        const num = parsePhoneNumberWithError(value);
        if (num?.isValid()) {
          return num.formatNational();
        }
      }
      return value.trim();
    } catch {
      // Return the original value without error markup for invalid numbers
      return value.trim() || "Unavailable";
    }
  }

  static validator(value: string) {
    try {
      const num = parsePhoneNumberWithError(value);
      if (num?.isValid()) {
        if (num.format("E.164") !== value) {
          return "Error: Not valid E.164 format.";
        }
        return true;
      }
    } catch (err: any) {
      return `Error: ${err.message}`;
    }
    return false;
  }

  static comparePhoneNumbers(phone1: string, phone2: string): boolean {
    try {
      if (!phone1 || !phone2) return false;

      // Handle SIP URIs - compare directly if both are SIP
      if (phone1.startsWith("sip:") && phone2.startsWith("sip:")) {
        return phone1 === phone2;
      }

      // If one is SIP and other is not, they're different
      if (phone1.startsWith("sip:") || phone2.startsWith("sip:")) {
        return false;
      }

      // Normalize phone numbers by removing all non-digit characters except +
      const normalizePhone = (phone: string): string =>
        phone.replace(/[^\d+]/g, "");

      const normalized1 = normalizePhone(phone1.trim());
      const normalized2 = normalizePhone(phone2.trim());

      // First try normalized string comparison
      if (normalized1 === normalized2) {
        return true;
      }

      // Also compare digits-only (handles +6531582371 vs 6531582371 — same number, different + prefix)
      const digitsOnly = (phone: string) => phone.replace(/\D/g, "");
      const digits1 = digitsOnly(phone1);
      const digits2 = digitsOnly(phone2);
      if (digits1.length > 0 && digits1 === digits2) {
        return true;
      }

      // Then try parsing with libphonenumber
      const num1 = parsePhoneNumberWithError(phone1.trim(), "US");
      const num2 = parsePhoneNumberWithError(phone2.trim(), "US");

      if (num1?.isValid() && num2?.isValid()) {
        // Compare E.164 format for accurate comparison
        return num1.format("E.164") === num2.format("E.164");
      }

      // If parsing fails, fall back to normalized comparison
      return normalized1 === normalized2;
    } catch {
      // If parsing fails for either number, compare normalized strings
      const normalizePhone = (phone: string): string =>
        phone.replace(/[^\d+]/g, "");
      return normalizePhone(phone1.trim()) === normalizePhone(phone2.trim());
    }
  }

  static extractPhone(value: string): string {
    if (!value) return value;
    const sipMatch = /^sip:(\+?\d+)@/i.exec(value);
    if (sipMatch) return sipMatch[1];
    const bracketMatch = /^\[[A-Z]{2,3}\](.+)$/.exec(value);
    if (bracketMatch) return bracketMatch[1].trim();
    return value;
  }
}

export default PhoneUtils;
